import { getDefaultStore } from 'jotai';
import { networkLogEnabledAtom, networkLogEntriesAtom, NetworkLogEntry } from '../atoms';

const MAX_NETWORK_LOG_ENTRIES = 100;
let networkLogIdCounter = 0;

/**
 * Repo calls are passed in as anonymous lambdas (e.g. `(repo) => repo.getAppFeatureFlags()`),
 * so the called method name is recovered from the lambda source rather than threading a label through every call site.
 * Matches the last `.method(` before the call parentheses, so chained access like `repo.api.closeStreamUrl(...)` resolves to "closeStreamUrl" instead of "api".
 */
export function getRepoMethodName(call: (...args: unknown[]) => unknown): string {
  const match = call.toString().match(/repo(?:\.\w+)*\.(\w+)\(/);
  return match ? match[1] : 'unknown';
}

export interface CapturedCall {
  method?: string;
  args?: unknown[];
}

/**
 * Wraps a repo object so the first method invoked through it (including nested access like `repo.api.xxx(...)`)
 * is recorded into `captured`, giving the actual argument values passed for the network log instead of just the method name.
 */
export function createCallRecordingProxy<T extends object>(target: T, captured: CapturedCall, path = ''): T {
  return new Proxy(target, {
    get(obj, prop) {
      const value = Reflect.get(obj, prop as keyof T);
      if (typeof prop !== 'string') return value;
      const currentPath = path ? `${path}.${prop}` : prop;
      if (typeof value === 'function') {
        return (...args: unknown[]) => {
          if (!captured.method) {
            captured.method = currentPath;
            captured.args = args;
          }
          return (value as (...a: unknown[]) => unknown).apply(obj, args);
        };
      }
      if (value !== null && typeof value === 'object') {
        return createCallRecordingProxy(value as object, captured, currentPath);
      }
      return value;
    }
  });
}

export function startNetworkLogEntry(
  cmd: string,
  method: string,
  params?: Record<string, unknown>
): number | undefined {
  const store = getDefaultStore();
  if (!store.get(networkLogEnabledAtom)) return undefined;

  const id = ++networkLogIdCounter;
  const entry: NetworkLogEntry = {
    id,
    timestamp: new Date().toISOString(),
    cmd,
    method,
    status: 'pending',
    params
  };
  store.set(networkLogEntriesAtom, (prev) => [entry, ...prev].slice(0, MAX_NETWORK_LOG_ENTRIES));
  return id;
}

export function finishNetworkLogEntry(id: number | undefined, patch: Partial<NetworkLogEntry>): void {
  if (id === undefined) return;
  const store = getDefaultStore();
  store.set(networkLogEntriesAtom, (prev) =>
    prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry))
  );
}

/**
 * Logs a single already-completed event, for callers outside the kmm/ntvb/kmmRequest choke points
 * (e.g. Flow subscriptions in flowSubscribe, which emit repeatedly and have no single start/end).
 */
export function logNetworkEvent(
  cmd: string,
  method: string,
  status: 'success' | 'error',
  payload: { response?: unknown; error?: unknown; params?: Record<string, unknown> } = {}
): void {
  const id = startNetworkLogEntry(cmd, method, payload.params);
  finishNetworkLogEntry(id, { status, response: payload.response, error: payload.error });
}

/** Turns a captured call's argument list into the Record shape NetworkLogEntry.params expects. */
export function argsToParams(args?: unknown[]): Record<string, unknown> | undefined {
  if (!args || args.length === 0) return undefined;
  return { args };
}
